import { useCallback, useEffect, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ExportMenu } from '@/components/report/ExportMenu';
import { exportColumns } from '@/components/report/groupedColumns';
import {
  headerFilterOptions,
  headerFiltersAreOffered,
  matchesHeaderFilters,
  withHeaderFilters,
  type HeaderFilterSelection,
} from '@/components/report/headerFilter';
import { useReportFilters } from '@/components/report/useReportFilters';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportColumn } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';

import { bulkTrimCardFilters } from './filters';

const REPORT_ID = 'bulk-trim-card';
const KEY_SEPARATOR = '\u0000';
const HEADER_FILTER_PREFIX = 'col.';
const NO_HEADER_FILTERS: HeaderFilterSelection = {};

interface BulkTrimCardRow extends Record<string, unknown> {
  season: string;
  working_no: string;
  parts: string;
  product_type: string;
  material_item: string;
  reference_code: string;
  material_function: string;
  specification: string;
  material_size: string;
  country: string | null;
}

const ROW_FIELDS = [
  'season',
  'working_no',
  'parts',
  'product_type',
  'material_item',
  'reference_code',
  'material_function',
  'specification',
  'material_size',
  'country',
] as const;
const HEADER_FILTER_FIELDS: readonly string[] = ROW_FIELDS;
const LIST_FILTER_FIELDS = new Set<string>(['parts', 'country']);
type RowField = (typeof ROW_FIELDS)[number];

function rowKey(row: BulkTrimCardRow): string {
  return ROW_FIELDS.map((field) => String(row[field] ?? '')).join(KEY_SEPARATOR);
}

function dimension(
  field: RowField,
  header: string,
): ColumnDef<BulkTrimCardRow> {
  return {
    id: field,
    accessorKey: field,
    header,
    meta: { exportLabel: header },
    cell: ({ row }) => String(row.original[field] ?? ''),
  };
}

export default function BulkTrimCardReport() {
  const { user, status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);
  const schema = useMemo(() => bulkTrimCardFilters(factory), [factory]);
  const { values, isComplete, setValue } = useReportFilters(schema);

  useEffect(() => {
    if (status !== 'authenticated' || searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  const filters = useMemo(
    () => ({
      factory: values.factory ?? null,
      b_number: values.b_number ?? null,
    }),
    [values],
  );

  const report = useReportQuery<BulkTrimCardRow>(REPORT_ID, filters, {
    enabled: isComplete,
  });
  const rows = useMemo(() => report.data?.rows ?? [], [report.data?.rows]);
  const articleColumns = useMemo(
    () => (report.data?.columns ?? []).filter(
      (column: ReportColumn) => column.group === 'Article no' && column.field !== 'article_anchor',
    ),
    [report.data?.columns],
  );

  const headerFilters = useMemo<HeaderFilterSelection>(() => {
    const selection: Record<string, string[]> = {};
    for (const field of HEADER_FILTER_FIELDS) {
      selection[field] = searchParams.getAll(`${HEADER_FILTER_PREFIX}${field}`);
    }
    return selection;
  }, [searchParams]);
  const offerHeaderFilters = headerFiltersAreOffered(report.data);
  const activeHeaderFilters = offerHeaderFilters ? headerFilters : NO_HEADER_FILTERS;
  const headerFilterOptionsByField = useMemo(
    () => headerFilterOptions(
      rows,
      HEADER_FILTER_FIELDS,
      LIST_FILTER_FIELDS,
    ),
    [rows],
  );
  const onHeaderFilterChange = useCallback(
    (field: string, next: string[]) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          const name = `${HEADER_FILTER_PREFIX}${field}`;
          params.delete(name);
          for (const value of next) params.append(name, value);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const tableRows = useMemo(
    () => offerHeaderFilters
      ? rows.filter((row) => matchesHeaderFilters(
          row,
          HEADER_FILTER_FIELDS,
          activeHeaderFilters,
          LIST_FILTER_FIELDS,
        ))
      : rows,
    [offerHeaderFilters, rows, activeHeaderFilters],
  );
  const hasActiveHeaderFilters = Object.values(activeHeaderFilters).some(
    (selected) => selected.length > 0,
  );

  const columns = useMemo<ColumnDef<BulkTrimCardRow>[]>(
    () => {
      const base: ColumnDef<BulkTrimCardRow>[] = [
        dimension('season', 'Season'),
        dimension('working_no', 'Working no'),
        dimension('parts', 'Parts'),
        dimension('product_type', 'Product type'),
        dimension('material_item', 'Material item'),
        dimension('reference_code', 'Reference code'),
        dimension('material_function', 'Function'),
        dimension('specification', 'Specification'),
        dimension('material_size', 'Material size'),
        {
          id: 'articles',
          header: 'Article no',
          meta: { exportGroup: 'Article no' },
          columns: articleColumns.map((article) => ({
            id: article.field,
            header: article.label,
            enableSorting: false,
            meta: { exportGroup: 'Article no', exportLabel: article.label },
            cell: ({ row }) => (
              <div className="max-w-24 whitespace-normal break-words">
                {String(row.original[article.field] ?? '')}
              </div>
            ),
          })),
        },
        dimension('country', 'Country'),
      ];
      return offerHeaderFilters
        ? withHeaderFilters(
            base,
            HEADER_FILTER_FIELDS,
            activeHeaderFilters,
            headerFilterOptionsByField,
            onHeaderFilterChange,
          )
        : base;
    },
    [
      articleColumns,
      offerHeaderFilters,
      activeHeaderFilters,
      headerFilterOptionsByField,
      onHeaderFilterChange,
    ],
  );

  const table = !isComplete ? (
    <DataTable
      columns={[]}
      data={[]}
      emptyMessage="Choose a factory and enter a B Number to run the report."
    />
  ) : (
    <DataTable
      columns={columns}
      data={tableRows}
      isLoading={report.isLoading}
      virtualized
      truncated={report.data?.truncated === true}
      getRowId={rowKey}
      caption="Bulk trim materials, with material colour shown under each article"
      emptyMessage="No bulk trim materials matched. Clear a column filter or check the B Number and try again."
    />
  );

  return (
    <ReportPage
      title="Bulk Trim Card"
      description={
        hasActiveHeaderFilters
          ? 'Bulk trim materials by article, colour and country. Column filters affect the table only, not exports.'
          : 'Bulk trim materials by article, colour and country.'
      }
      filters={
        <FilterBar
          schema={schema}
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={{ filters }}
              columns={exportColumns(columns)}
              disabled={!isComplete}
            />
          }
        />
      }
      table={table}
      isLoading={report.isLoading}
      error={report.error}
      generatedAt={report.data?.generatedAt ?? null}
      dataAsOf={report.data?.dataAsOf ?? null}
      truncated={report.data?.truncated ?? false}
    />
  );
}
